import { supabase } from "@/integrations/supabase/client";
import { Tables } from "@/integrations/supabase/types";

export type FormField = Tables<"form_fields">;

export interface FormFieldInput {
  title: string;
  instructions?: string;
  is_required: boolean;
  position: number;
  field_type: string;
  answer_type: string;
  options?: string[] | null;
  agent_id: string;
}

export interface ContactInfoSettings {
  requirePhoneNumber?: boolean;
  requireName?: boolean;
  requireEmail?: boolean;
}

export const formFieldsService = {
  /**
   * Fetch all form fields for an agent (regular or voice)
   */
  async getFormFields(agentId: string, isVoiceAgent?: boolean): Promise<{
    data: FormField[] | null;
    error: Error | null;
  }> {
    try {
      const query = supabase
        .from("form_fields")
        .select("*")
        .order("position", { ascending: true });

      // Use appropriate field based on agent type
      if (isVoiceAgent) {
        query.eq("voice_agent_id", agentId);
      } else {
        query.eq("agent_id", agentId);
      }

      const { data, error } = await query;

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Error fetching form fields:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Create a new form field for an agent (regular or voice)
   */
  async createFormField(fieldData: FormFieldInput & { isVoiceAgent?: boolean }): Promise<{
    data: FormField | null;
    error: Error | null;
  }> {
    try {
      const { isVoiceAgent, ...dbFieldData } = fieldData;
      
      // Set the appropriate agent field based on type
      const insertData = isVoiceAgent 
        ? { ...dbFieldData, voice_agent_id: fieldData.agent_id, agent_id: null }
        : { ...dbFieldData, agent_id: fieldData.agent_id, voice_agent_id: null };

      const { data, error } = await supabase
        .from("form_fields")
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Error creating form field:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update an existing form field
   */
  async updateFormField(
    fieldId: string,
    fieldData: Partial<FormFieldInput>,
  ): Promise<{
    data: FormField | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("form_fields")
        .update(fieldData)
        .eq("id", fieldId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error("Error updating form field:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Delete a form field
   */
  async deleteFormField(fieldId: string): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("form_fields")
        .delete()
        .eq("id", fieldId);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      console.error("Error deleting form field:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update field positions after reordering
   */
  async updateFieldPositions(
    fields: Array<{ id: string; position: number }>,
  ): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      // Update positions in batch
      const updates = fields.map((field) =>
        supabase
          .from("form_fields")
          .update({ position: field.position })
          .eq("id", field.id),
      );

      await Promise.all(updates);

      return { success: true, error: null };
    } catch (error) {
      console.error("Error updating field positions:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Get agent's contact info settings
   */
  async getContactInfoSettings(agentId: string): Promise<{
    data: ContactInfoSettings | null;
    error: Error | null;
  }> {
    try {
      const { data, error } = await supabase
        .from("agents")
        .select("contact_info")
        .eq("id", agentId)
        .single();

      if (error) throw error;

      return {
        data: (data.contact_info as ContactInfoSettings) || {},
        error: null,
      };
    } catch (error) {
      console.error("Error fetching contact info settings:", error);
      return {
        data: null,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },

  /**
   * Update agent's contact info settings
   */
  async updateContactInfoSettings(
    agentId: string,
    settings: ContactInfoSettings,
  ): Promise<{
    success: boolean;
    error: Error | null;
  }> {
    try {
      const { error } = await supabase
        .from("agents")
        .update({ contact_info: settings })
        .eq("id", agentId);

      if (error) throw error;

      return { success: true, error: null };
    } catch (error) {
      console.error("Error updating contact info settings:", error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error("Unknown error"),
      };
    }
  },
};
